import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { StoreApi } from 'zustand';

import { useAskUiStoreApi } from '@/components/libraryChat/LibraryChatContext';
import { useRealtimeChannel } from '@/hooks/realtime/useRealtimeChannel';
import type { AskConversationSnapshot } from '@/services/ask/api';
import { askKeys, forgetAskConversation, refreshAskHistory } from '@/services/ask/queryOptions';
import {
  askChannelFor,
  changesHistory,
  parseAskRealtimeEvent,
  type AskRealtimeEvent,
} from '@/services/ask/realtimeEvents';
import { getIdToken } from '@/services/auth';
import { parseIdTokenClaims } from '@/services/session';
import type { AskUiStore } from '@/services/stores/askUiStore';

/**
 * Applies one hint to the cache. A hint never supplies content: it decides
 * whether the snapshot is re-read. An event whose `version` is not newer than
 * the cached snapshot's describes a state the app already holds, so it costs
 * nothing.
 *
 * `turn.progress` also writes its stage into the cached turn, so the stepper
 * moves the moment the hint lands rather than a round trip later. The refetch
 * that follows overwrites it either way.
 *
 * A delete is only flagged as "elsewhere" when this device still held the
 * conversation and is not deleting it itself. The server can publish the hint
 * before the delete's own response arrives, so the local delete marks the id
 * first, and the device that deleted never tells its reader someone else did.
 */
export function applyAskEvent(
  queryClient: QueryClient,
  store: StoreApi<AskUiStore>,
  event: AskRealtimeEvent
): void {
  const { conversationId, version } = event.data;

  const key = askKeys.snapshot(conversationId);

  const cached = queryClient.getQueryData<AskConversationSnapshot>(key);

  if (event.type === 'conversation.deleted') {
    if (cached && !store.getState().deletingHere.has(conversationId)) {
      store.getState().markDeletedElsewhere(conversationId);
    }

    forgetAskConversation(queryClient, conversationId);

    return;
  }

  if (cached && version <= cached.version) {
    return;
  }

  if (cached && event.type === 'turn.progress') {
    queryClient.setQueryData<AskConversationSnapshot>(key, {
      ...cached,
      turns: cached.turns.map((turn) =>
        turn.id === event.data.turnId
          ? { ...turn, status: 'running', stage: event.data.stage }
          : turn
      ),
    });
  }

  queryClient.invalidateQueries({ queryKey: key, exact: true }).catch(() => {});

  if (changesHistory(event)) {
    refreshAskHistory(queryClient);
  }
}

const ASK_REALTIME_MONITORING = {
  breadcrumbCategory: 'ask.realtime',
  refusedMessage: 'Ask realtime subscription refused',
  component: 'AskRealtime',
  source: 'ask-realtime',
};

async function requireIdToken(): Promise<string> {
  const token = await getIdToken();

  if (!token) {
    throw new Error('No id token for the realtime connection');
  }

  return token;
}

/**
 * Follows every one of the signed-in user's conversations on `/ask/{sub}/*`
 * while the Ask layout is mounted.
 *
 * One wildcard subscription rather than one per conversation: it is already
 * open before a new conversation's first turn has a channel to name, it keeps
 * history current, and switching conversations needs no resubscribe.
 *
 * `{sub}` comes from the id token the socket authenticates with, never from an
 * API user id, because the channel is authorized against that token. A staff
 * member acting as a user therefore follows their own channel, which the
 * server also publishes acted-as turns to.
 *
 * The connection state lands in the Ask store, where it tells the snapshot
 * query whether to poll.
 *
 * `enabled` is the Ask screen's focus, not its presence. A stack keeps a
 * screen mounted beneath whatever is pushed over it, and Close pushes the
 * Inquiry Library over Ask whenever there is no history to go back to (a deep
 * link, a reload, the sign-in redirect), so a mounted-but-hidden Ask would
 * otherwise hold a second socket open for as long as the tab lives.
 */
export function useAskRealtime(onResume: () => void, { enabled }: { enabled: boolean }): void {
  const queryClient = useQueryClient();

  const store = useAskUiStoreApi();

  useRealtimeChannel<AskRealtimeEvent>({
    enabled,
    identity: undefined,
    getToken: requireIdToken,
    channelFor: (token) => {
      const sub = parseIdTokenClaims(token)?.sub;

      return sub ? askChannelFor(sub) : null;
    },
    parse: parseAskRealtimeEvent,
    onEvent: (event) => applyAskEvent(queryClient, store, event),
    onResume,
    onConnectionChange: store.getState().setConnection,
    monitoring: ASK_REALTIME_MONITORING,
  });
}
